import type { ReactNode } from "react";

import {
  SITE_URL,
  ORG_ID,
  orgNode,
  faqJsonld,
  breadcrumbJsonld,
  webPageNode,
  type LearnFAQ,
  type LearnPageData,
} from "./learnShared";

const P = (s: ReactNode): ReactNode => <p>{s}</p>;

const faqs: LearnFAQ[] = [
  {
    question: "Is there SAINT® TMS near Miami & Miami Beach?",
    answer:
      "Yes. Neuro Wellness TMS Centers of America is a Verified SAINT® Provider in Coral Springs, Florida, roughly 45–60 minutes north of Miami & Miami Beach by car. There is no Miami & Miami Beach office — all treatment is delivered at the Coral Springs clinic. Because SAINT® is a five-day protocol rather than a six-week daily course, many patients from Miami-Dade County drive up once and stay locally for the treatment week. You can confirm other verified South Florida providers through the Magnus Medical provider locator.",
  },
  {
    question: "How far is the clinic from Miami & Miami Beach?",
    answer:
      "Coral Springs is approximately 45–60 minutes from Miami & Miami Beach by car, depending on traffic and your starting point. Patients from Miami Beach, Surfside, Bal Harbour, and Aventura typically drive north via I-95 or the Florida Turnpike. Some patients from Golden Beach or Aventura find the drive shorter than those starting from South Beach or Key Biscayne. We can help you plan the route and discuss whether staying near the clinic for the week makes sense for you.",
  },
  {
    question: "Can I go home each night during treatment?",
    answer:
      "It is possible, but most Miami & Miami Beach patients prefer not to. SAINT® involves ten sessions per day with roughly 50-minute breaks between them, which means you are at the clinic for most of each day. Driving approximately 45–60 minutes each way on top of a full treatment day can be tiring. Many patients stay at a hotel near the clinic for the five-day week. A companion can also help with driving.",
  },
  {
    question: "Does insurance cover SAINT®?",
    answer:
      "SAINT® may be covered; coverage varies by plan. SAINT® is billed under Category III CPT codes, and some insurers do not yet cover it, so it is often self-pay. Neuro Wellness verifies your benefits for free, whatever carrier you have, and confirms what your plan allows before you commit. Coverage depends on your plan, not your city of residence. Financing is available on a case-by-case basis and discussed during consultation.",
  },
  {
    question:
      "What if I live in Miami & Miami Beach and SAINT® is not right for me?",
    answer:
      "Neuro Wellness also offers standard TMS, SPRAVATO® (esketamine), Prism for PTSD, IV ketamine infusions, psychotherapy, and medication management. If the clinical team determines that SAINT® is not appropriate for your situation, they can help you explore other options. A free consultation is the best way to get answers specific to your history and circumstances.",
  },
  {
    question:
      "Do I need to complete anything before traveling from Miami & Miami Beach for SAINT®?",
    answer:
      "Yes. Before the five-day treatment week, you need a psychiatric consultation and a functional MRI (fMRI) scan. Some patients complete the consultation remotely and travel to Coral Springs only for the MRI and treatment week. The care team can help coordinate these steps and discuss what can be done from Miami & Miami Beach versus what requires an in-person visit.",
  },
];

const comparisonTable: ReactNode = (
  <div className="my-6 overflow-x-auto">
    <table className="w-full border-collapse text-sm">
      <thead>
        <tr className="border-b-2 border-border text-left">
          <th className="py-2 pr-4 font-bold text-primary">Option</th>
          <th className="py-2 pr-4 font-bold text-primary">Where</th>
          <th className="py-2 pr-4 font-bold text-primary">Days in clinic</th>
          <th className="py-2 pr-4 font-bold text-primary">
            Total time commitment
          </th>
          <th className="py-2 pr-4 font-bold text-primary">Cost / coverage</th>
        </tr>
      </thead>
      <tbody>
        <tr className="border-b border-border">
          <td className="py-2 pr-4 font-medium">SAINT® (Magnus)</td>
          <td className="py-2 pr-4">
            Coral Springs — ~45–60 min from Miami & Miami Beach
          </td>
          <td className="py-2 pr-4">5 consecutive days</td>
          <td className="py-2 pr-4">10 sessions/day, ~50 total</td>
          <td className="py-2 pr-4">
            Starts at $25,000; may be covered, varies by plan
          </td>
        </tr>
        <tr className="border-b border-border">
          <td className="py-2 pr-4 font-medium">Accelerated TMS (non-SAINT)</td>
          <td className="py-2 pr-4">Varies — some South Florida clinics</td>
          <td className="py-2 pr-4">1–2 weeks</td>
          <td className="py-2 pr-4">Multiple sessions/day</td>
          <td className="py-2 pr-4">
            Typically lower; does not use SAINT® targeting
          </td>
        </tr>
        <tr className="border-b border-border">
          <td className="py-2 pr-4 font-medium">Standard TMS</td>
          <td className="py-2 pr-4">
            Coral Springs — ~45–60 min from Miami & Miami Beach
          </td>
          <td className="py-2 pr-4">4–6 weeks</td>
          <td className="py-2 pr-4">1 session/day, ~36 total</td>
          <td className="py-2 pr-4">More often covered by insurance</td>
        </tr>
      </tbody>
    </table>
  </div>
);

export const saintMiami: LearnPageData = {
  slug: "saint-tms-miami",
  title:
    "SAINT® TMS Near Miami & Miami Beach | 5-Day Treatment in Coral Springs, FL",
  description:
    "Looking for SAINT® TMS near Miami & Miami Beach? Neuro Wellness in Coral Springs offers the FDA-cleared 5-day SAINT® protocol. See drive times, cost and insurance.",
  canonical: "/locations/saint-tms-miami",
  h1: "SAINT® TMS Near Miami & Miami Beach: 5-Day Treatment in Coral Springs",
  breadcrumbLabel: "SAINT® TMS Near Miami & Miami Beach",
  crumbs: [
    { name: "Home", href: "/" },
    { name: "Patient Education", href: "/learn" },
    { name: "SAINT® TMS Near Miami & Miami Beach", href: "" },
  ],

  shortAnswer:
    "Yes — SAINT® TMS is available near Miami & Miami Beach. Neuro Wellness TMS Centers of America is a Verified SAINT® Provider in Coral Springs, Florida, roughly 45–60 minutes north by car. SAINT® is an FDA-cleared, fMRI-guided accelerated protocol that delivers 50 sessions over five consecutive days rather than the usual four to six weeks. Because the schedule is compressed, many patients from Miami-Dade County drive up once and stay locally for the treatment week instead of commuting daily. SAINT® at Neuro Wellness starts at $25,000; coverage varies by plan, and we verify your benefits for free.",

  keyTakeaways: [
    "Neuro Wellness does not have a Miami & Miami Beach office — the clinic is in Coral Springs, roughly 45–60 minutes north by car.",
    "SAINT® is an FDA-cleared, fMRI-guided accelerated protocol: 10 sessions per day for 5 consecutive days, about 50 sessions total.",
    "The compressed five-day schedule can make a single trip more practical than daily commuting for six weeks of standard TMS.",
    "SAINT® at Neuro Wellness starts at $25,000; coverage varies by plan, and we verify your benefits for free.",
    "A psychiatric consultation and fMRI scan are required before the treatment week begins.",
  ],

  sections: [
    {
      heading:
        "Is SAINT® Available Near Miami & Miami Beach? Yes — a Verified Provider in Coral Springs",
      body: (
        <>
          {P(
            "If you are searching for SAINT® TMS near Miami & Miami Beach, the closest verified option is Neuro Wellness TMS Centers of America, located at 9734 W Sample Rd, 2nd Floor, Suite B, Coral Springs, FL 33065. Coral Springs sits in northern Broward County, about 45–60 minutes north of Miami & Miami Beach by car depending on traffic and your starting point. There is no Neuro Wellness office in Miami & Miami Beach — all treatment is delivered at the Coral Springs clinic.",
          )}
          {P(
            "Neuro Wellness is a Verified SAINT® Provider in Coral Springs, Florida. That means the clinic uses the Magnus Neuromodulation System with SAINT® Technology — the FDA-cleared targeting system — rather than a generic accelerated schedule on a conventional device. SAINT® (Stanford Accelerated Intelligent Neuromodulation Therapy) received FDA clearance (510(k) K220177) for major depressive disorder in adults whose depression has not improved enough on prior antidepressant medication. Neuro Wellness treats adults 22 and older.",
          )}
          {P(
            "Other verified SAINT® providers may exist in South Florida. You can confirm the current list through the official Magnus Medical provider locator. If a verified provider is closer to Miami & Miami Beach than Coral Springs, that option may involve less travel. We describe the Coral Springs option here so you can compare what the week involves, what it costs, and what to plan for.",
          )}
          {P(
            "Serving patients from Miami, Miami Beach, South Beach, Fisher Island, Star Island, Indian Creek, Bal Harbour, Bay Harbor Islands, Surfside, Key Biscayne, Golden Beach, Aventura, and Sunny Isles Beach — the five-day format makes the drive north practical for many people who would otherwise face six weeks of daily trips for standard TMS.",
          )}
        </>
      ),
    },
    {
      heading: "What the 5-Day Week Looks Like When You Live Nearby",
      body: (
        <>
          {P(
            "SAINT® compresses what is normally a four-to-six-week course of transcranial magnetic stimulation (TMS) into five consecutive days. Each day includes 10 treatment sessions, each lasting a few minutes, with roughly 50-minute breaks between them. The full course is about 50 sessions total, using a pulse pattern called intermittent theta burst stimulation (iTBS).",
          )}
          {P(
            "If you live in Miami & Miami Beach, you have two practical options for the week. The first is to drive up each day. With a 45–60 minute drive each way on top of a full treatment day, that can be tiring, and most patients from Miami-Dade County prefer not to do it five days in a row. The second option is to stay near the clinic in Coral Springs for the week. Because the whole protocol is only five days, a short hotel stay is often more manageable than six weeks of daily commuting for standard TMS.",
          )}
          {P(
            "A support person can accompany you during the week. Many patients from Miami & Miami Beach bring a family member or friend who helps with driving, meals, and company between sessions. The clinic can share information about nearby lodging, though specific hotel recommendations and any travel discounts should be confirmed directly with the clinic.",
          )}
          {P(
            "Each treatment session is brief and non-invasive. You sit comfortably while a magnetic coil delivers targeted stimulation to a specific area of the brain. There is no anesthesia and no sedation, so you are awake and alert throughout. Most people drive themselves home after a session, though having a companion for the first day or two is common.",
          )}
        </>
      ),
    },
    {
      heading:
        "SAINT® vs Other Accelerated and Standard TMS Options in South Florida",
      body: (
        <>
          {P(
            "Not every accelerated TMS protocol is SAINT®. Some clinics in South Florida offer accelerated schedules using conventional TMS devices without the FDA-cleared SAINT® targeting system. These protocols can copy the compressed schedule — multiple sessions per day over one to two weeks — but they do not use the fMRI-guided personalized targeting that defines SAINT® on the Magnus Neuromodulation System.",
          )}
          {P(
            "The difference matters because the evidence behind SAINT® comes from trials that used the Magnus system with fMRI-guided targeting. In the 2022 randomized trial, 79% of participants reached remission (Cole et al., Am J Psychiatry 2022;179(2):132–141). Individual results may vary. Those results have not been replicated for non-SAINT accelerated protocols, and the outcomes from SAINT® trials do not transfer to them. If a clinic offers accelerated TMS without using the SAINT® system, ask for its own published outcome data for the specific protocol and device it uses.",
          )}
          {P(
            "Standard TMS is another option. It delivers one session per day, five days a week, for about four to six weeks — roughly 36 sessions total. Standard TMS has a larger evidence base and is more often covered by insurance. The trade-off is time: six weeks of daily visits versus five days for SAINT®. For someone driving from Miami & Miami Beach, six weeks of daily commuting is a significant commitment, which is one reason the five-day SAINT® format appeals to patients who can manage the travel.",
          )}
          {P(
            "Neuro Wellness also offers SPRAVATO® (esketamine), IV ketamine infusions, Prism for PTSD, psychotherapy, and medication management. These are different treatments with different evidence, costs, and schedules. A consultation can help you understand which option may fit your clinical situation and circumstances.",
          )}
        </>
      ),
    },
    {
      heading:
        "Cost (Starting at $25,000) and Insurance: Free Benefits Verification",
      body: (
        <>
          {P(
            "SAINT® at Neuro Wellness starts at $25,000. That price includes the fMRI scan, personalized targeting, 50 sessions over five days, physician oversight, and follow-up. Financing is available on a case-by-case basis and discussed during consultation. We never quote a lower or exact final price without confirming your specific situation.",
          )}
          {P(
            "SAINT® may be covered; coverage varies by plan. SAINT® is billed under Category III CPT codes, and some insurers do not yet cover it, so it is often self-pay. Neuro Wellness verifies your benefits for free, whatever carrier you have, and confirms what your plan allows before you commit. Coverage depends on your plan, not your city of residence — whether you live in Miami Beach, Aventura, or Sunny Isles Beach does not change what your policy covers.",
          )}
          {P(
            "Medicare and FEHB (Federal Employees Health Benefits Program) pathways may apply. If you have Medicare or a federal plan, we verify those benefits the same way. Conventional TMS is more often covered by insurance than SAINT®, which is worth weighing when comparing the two. You can start with a free benefits check at any time, and there is no obligation to proceed with treatment.",
          )}
          {P(
            "Lower-priced accelerated protocols exist in the market — some marketed at roughly a third of SAINT®'s price. The trade-off is that those protocols typically do not use the FDA-cleared SAINT® targeting system, and the SAINT® trial outcomes do not apply to them. We address that comparison directly rather than avoiding it: the higher price reflects the fMRI-guided targeting, the Magnus Neuromodulation System, and the trial evidence that supports SAINT® specifically.",
          )}
        </>
      ),
    },
    {
      heading:
        "Before and After the Week: Consultation, MRI, and Follow-Up Close to Home",
      body: (
        <>
          {P(
            "Before the five-day treatment week, two steps are required: a psychiatric consultation and a functional MRI (fMRI) scan. The consultation determines whether SAINT® is clinically appropriate for you. SAINT® is FDA-cleared for adults with major depressive disorder whose depression has not improved enough on prior antidepressant medication. Neuro Wellness treats adults 22 and older. SAINT® is not cleared for anxiety, OCD, PTSD, bipolar disorder, concussion, or any other condition, and eligibility is clinical only.",
          )}
          {P(
            "Some patients from Miami & Miami Beach complete the consultation remotely and travel to Coral Springs only for the fMRI and the treatment week. The care team can help coordinate what can be done from home versus what requires an in-person visit. The fMRI scan maps each person's individual treatment target — a personalized approach rather than a one-size-fits-all scalp measurement.",
          )}
          {P(
            "After the five-day week, follow-up continues. Because SAINT® is delivered in a compressed format, ongoing monitoring helps the clinical team track your response and adjust any medication or therapy as needed. Some follow-up can be handled remotely, which reduces the number of trips from Miami & Miami Beach. The care team discusses the follow-up schedule with you before treatment begins.",
          )}
          {P(
            "If you are ready to explore whether SAINT® may fit your situation, the first step is a free consultation. You can start with our free candidacy screener or check your insurance benefits at no cost. Call 954-827-9793 to speak with the team.",
          )}
        </>
      ),
    },
    {
      heading: "Comparison: Your Options at a Glance",
      body: (
        <>
          {P(
            "The table below summarizes the key differences between SAINT®, other accelerated TMS protocols, and standard TMS for someone considering treatment from the Miami & Miami Beach area.",
          )}
          {comparisonTable}
        </>
      ),
    },
  ],

  faqs,
  sources: [
    {
      citation:
        "Cole EJ, Stimpson KH, Bentzley BS, et al. Stanford Neuromodulation Therapy (SNT): A Double-Blind Randomized Controlled Trial. Am J Psychiatry. 2022;179(2):132–141.",
      url: "https://pubmed.ncbi.nlm.nih.gov/35624717/",
    },
    {
      citation:
        "Cole EJ, Phillips AL, Bentzley BS, et al. Stanford Accelerated Intelligent Neuromodulation Therapy for treatment-resistant depression. Am J Psychiatry. 2020;177(8):716–726.",
      url: "https://pubmed.ncbi.nlm.nih.gov/32472615/",
    },
    {
      citation:
        "U.S. FDA. 510(k) Premarket Notification K220177. Magnus Neuromodulation System. September 2022.",
      url: "https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpmn/pmn.cfm?ID=K220177",
    },
    {
      citation:
        "Magnus Medical. SAINT® Neuromodulation System — Breakthrough Device designation and FDA clearance.",
      url: "https://www.magnusmedical.com",
    },
  ],

  schemaJson: {
    "@context": "https://schema.org",
    "@graph": [
      webPageNode({
        id: `${SITE_URL}/locations/saint-tms-miami`,
        name: "SAINT® TMS Near Miami & Miami Beach: 5-Day Treatment in Coral Springs",
        url: `${SITE_URL}/locations/saint-tms-miami`,
        description:
          "Looking for SAINT® TMS near Miami & Miami Beach? Neuro Wellness in Coral Springs offers the FDA-cleared 5-day SAINT® protocol. See drive times, cost and insurance.",
        about: [
          { "@id": `${SITE_URL}/locations/saint-tms-miami#therapy` },
          { "@id": `${SITE_URL}/#condition-mdd` },
        ],
        reviewedBy: {
          name: "Daniel Pietras, MD",
          url: `${SITE_URL}/dr-daniel-pietras-md`,
        },
        lastReviewed: "2026-11-03",
      }),
      orgNode,
      {
        "@type": "MedicalTherapy",
        "@id": `${SITE_URL}/locations/saint-tms-miami#therapy`,
        name: "Stanford Accelerated Intelligent Neuromodulation Therapy (SAINT®)",
        alternateName: ["SAINT TMS", "Stanford Neuromodulation Therapy (SNT)"],
        description:
          "Accelerated, fMRI-guided intermittent theta burst stimulation delivered on the Magnus Neuromodulation System with SAINT Technology (FDA 510(k) K220177): 10 sessions per day for 5 consecutive days.",
        relevantSpecialty: "Psychiatric",
      },
      {
        "@type": "MedicalCondition",
        "@id": `${SITE_URL}/#condition-mdd`,
        name: "Major depressive disorder",
        code: { "@type": "MedicalCode", code: "D003865", codingSystem: "MeSH" },
      },
      faqJsonld(faqs, `${SITE_URL}/locations/saint-tms-miami#faq`),
      breadcrumbJsonld([
        { name: "Home", href: "/" },
        { name: "Patient Education", href: "/learn" },
        {
          name: "SAINT® TMS Near Miami & Miami Beach",
          href: "/locations/saint-tms-miami",
        },
      ]),
      {
        "@type": "MedicalClinic",
        "@id": ORG_ID,
        name: "Neuro Wellness TMS Centers of America",
        url: SITE_URL,
        telephone: "+1-954-827-9793",
        address: {
          "@type": "PostalAddress",
          streetAddress: "9734 W Sample Rd, 2nd Floor, Suite B",
          addressLocality: "Coral Springs",
          addressRegion: "FL",
          postalCode: "33065",
          addressCountry: "US",
        },
        areaServed: [
          "Miami, FL",
          "Miami Beach, FL",
          "South Beach, FL",
          "Fisher Island, FL",
          "Star Island, FL",
          "Indian Creek, FL",
          "Bal Harbour, FL",
          "Bay Harbor Islands, FL",
          "Surfside, FL",
          "Key Biscayne, FL",
          "Golden Beach, FL",
          "Aventura, FL",
          "Sunny Isles Beach, FL",
        ],
      },
    ],
  },

  relatedLinks: [
    {
      title: "SAINT® TMS Locations",
      href: "/learn/saint-tms-locations",
      description: "Verified providers and how to confirm a real SAINT® clinic",
    },
    {
      title: "SAINT® TMS Cost",
      href: "/learn/saint-tms-cost",
      description: "Price, insurance, Medicare, and financing",
    },
    {
      title: "SAINT® vs Accelerated TMS",
      href: "/learn/saint-vs-accelerated-tms",
      description:
        "What's the same and what isn't between SAINT® and non-SAINT protocols",
    },
    {
      title: "SAINT® Accelerated TMS",
      href: "/saint",
      description: "The 5-day, fMRI-guided protocol offered at our clinic",
    },
    {
      title: "Am I a Candidate?",
      href: "/am-i-a-candidate-screener",
      description: "Free candidacy screener",
    },
    {
      title: "Check Insurance Options",
      href: "/check-insurance-options",
      description: "Free benefits verification",
    },
  ],

  reviewBlock: {
    writtenBy: "Neuro Wellness TMS Centers of America",
    reviewedBy: "Daniel Pietras, MD",
    reviewedByHref: "/dr-daniel-pietras-md",
    published: "November 3, 2026",
    lastReviewed: "November 3, 2026",
    lastUpdated: "November 3, 2026",
  },

  toc: [
    { label: "Quick Answer", href: "#quick-answer" },
    {
      label: "Is SAINT® Available Near Miami & Miami Beach?",
      href: "#is-saint-available-near-miami-miami-beach-yes-a-verified-provider-in-coral-springs",
    },
    {
      label: "What the 5-Day Week Looks Like",
      href: "#what-the-5-day-week-looks-like-when-you-live-nearby",
    },
    {
      label: "SAINT® vs Other TMS Options",
      href: "#saint-vs-other-accelerated-and-standard-tms-options-in-south-florida",
    },
    {
      label: "Cost and Insurance",
      href: "#cost-starting-at-25-000-and-insurance-free-benefits-verification",
    },
    {
      label: "Before and After the Week",
      href: "#before-and-after-the-week-consultation-mri-and-follow-up-close-to-home",
    },
    {
      label: "Comparison Table",
      href: "#comparison-your-options-at-a-glance",
    },
    { label: "FAQ", href: "#faq" },
    { label: "Cited Research", href: "#cited-research" },
  ],

  safetyBlock:
    "SAINT® is not for everyone. It is contraindicated for people with non-removable magnetic-sensitive metal in or near the head, certain implanted devices, or increased intracranial pressure. Possible side effects include headache, fatigue, scalp discomfort and anxiety; TMS carries a small risk of seizure and, as with any depression treatment, a risk of worsening depression or suicidal thoughts. If you are in crisis, call or text 988.",

  trademarkFooter:
    "SAINT® is a registered trademark of Stanford University, exclusively licensed to Magnus Medical, Inc. NWCOA is not affiliated with Stanford University.",

  ctaBand: {
    headline: "See if SAINT® is a fit, free",
    primaryText: "Learn If It's a Fit",
    primaryHref: "/am-i-a-candidate-screener",
    secondaryText: "Check my insurance",
    secondaryHref: "/check-insurance-options",
    phone: "954-827-9793",
  },
};
